import React, { useEffect, useState } from 'react';
import { EntityField, SelectedEntityFields } from '../../../models/query-builder';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useSaveData } from '../../../hooks/useSaveData';
import { queryBuilderApiKeyStore } from '../../../api/query-builder-api-key-store';
import { LoadingIndicator } from '../../../components/loading-indicator.component';
import { useLoadData } from '../../../hooks/useLoadData';
import { ApiResponse } from '../../../models/api-response';
import { useParams } from 'react-router-dom';
import { useAuthorize } from '../../../hooks/use-authorize';
import { UserPermissionName } from '../../../models/user-permission-name';

interface ColumnOptionsModalProps {
  isVisible: boolean;
  onModalClose: () => void;
  onSubmit: (selectedFields: Record<number, EntityField>) => void;
  selectedEntityFields: Record<number, EntityField>;
  setSelectedEntityFields: (entityFields: SelectedEntityFields) => void;
}

const ColumnOptionsModal = ({
  isVisible = false,
  onModalClose,
  onSubmit,
  selectedEntityFields,
  setSelectedEntityFields,
}: ColumnOptionsModalProps) => {
  const [checkedColumns, setCheckedColumns] = useState<Record<number, EntityField>>({
    ...selectedEntityFields,
  });

  const { id } = useParams();
  const { data, isLoading: isQueryLoading } = useLoadData<ApiResponse<any>>(queryBuilderApiKeyStore.getQuery(id));

  const { isLoading, mutateAsync } = useSaveData<EntityField[]>(queryBuilderApiKeyStore.entityFieldGetAll('column'));

  const [entityFields, setEntityFields] = useState<EntityField[]>([]);

  useEffect(() => {
    if (data) {
      if (data?.data?.qbFields?.length > 0) {
        const qbFields: EntityField[] = data?.data?.qbFields ?? [];
        const defaultSelectedFields = qbFields?.reduce<SelectedEntityFields>((columns, field) => {
          columns[field.entityFieldId] = field;
          return columns;
        }, {});
        setCheckedColumns(defaultSelectedFields);
      }
    }
  }, [data])

  useEffect(() => {
    mutateAsync({ data: {} }).then((data) => {
      if (data?.data?.length > 0) {
        setEntityFields(data?.data);
        const defaultSelectedFields = data?.data?.reduce<SelectedEntityFields>((columns, field) => {
          if (field.selectedByDefault) {
            columns[field.entityFieldId] = field;
          }
          return columns;
        }, {});
        setSelectedEntityFields(defaultSelectedFields);
      }
    });
  }, []);

  const handleSelectAllColumns = (event: any) => {
    if (event.target.checked) {
      setCheckedColumns(
        entityFields.reduce<Record<number, EntityField>>((columns, field) => {
          columns[field.entityFieldId] = field;
          return columns;
        }, {})
      );
    } else {
      setCheckedColumns({});
    }
  };

  const handleSelectColumn = (event: any, index: number) => {
    const updatedCheckedColumns = { ...checkedColumns };
    const field = entityFields[index];

    if (event.target.checked) {
      updatedCheckedColumns[field.entityFieldId] = field;
    } else {
      delete updatedCheckedColumns[field.entityFieldId];
    }
    setCheckedColumns(updatedCheckedColumns);
  };

  const handleSubmit = () => {
    onSubmit({ ...checkedColumns });
    onModalClose();
  };

  const hasSelectAllSelected = Object.keys(checkedColumns).length === entityFields.length;

  const isReadOnlyPermission = useAuthorize([UserPermissionName?.READ_ONLY]);

  return (
    <Dialog
      open={isVisible}
      fullWidth={true}
      maxWidth={'md'}
      // onClose={onCloseClick}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <DialogTitle id="alert-dialog-title">Column Options</DialogTitle>
      <IconButton
        aria-label="close"
        onClick={onModalClose}
        sx={{
          position: 'absolute',
          right: 8,
          top: 8,
          color: (theme) => theme.palette.grey[500],
        }}
      >
        <Tooltip title={<Typography variant="body1">Close</Typography>}>
          <CloseIcon />
        </Tooltip>
      </IconButton>
      <DialogContent dividers className="popupDilog">
        {(isLoading || isQueryLoading) && <LoadingIndicator isLoading={isLoading || isQueryLoading} />}
        {/* <ModalWithCloseIcon isOpen={isVisible} handleModalClose={onModalClose} modalTitle="Column Options" width="580px"> */}
        <Box sx={{ background: 'white', minWidth: '500px' }}>
          <Divider />
          <Grid item style={{ height: '400px', overflow: 'auto' }}>
            <Box style={{ width: '80%' }} sx={{ m: 3 }}>
              {!!entityFields.length && (
                <FormControlLabel
                  control={
                    <Checkbox checked={hasSelectAllSelected} name="selectAll" onChange={handleSelectAllColumns} />
                  }
                  label="Select All"
                />
              )}
              <Box sx={{ display: 'grid' }}>
                {entityFields.map((field, index) => (
                  <FormControlLabel
                    key={field.entityFieldId}
                    control={
                      <Checkbox
                        key={field.entityFieldId}
                        checked={!!checkedColumns[field.entityFieldId]}
                        onChange={(event) => handleSelectColumn(event, index)}
                        name={field.fieldName}
                      />
                    }
                    label={field.fieldDisplayName}
                  />
                ))}
              </Box>
            </Box>
          </Grid>
          <Divider />
          <Grid item margin={1} display="flex" justifyContent="flex-end">
            <Button onClick={onModalClose} style={{ marginRight: 3 }} variant="outlined">
              Cancel
            </Button>
            <Button onClick={handleSubmit} style={{ marginLeft: 3 }} variant="contained"
              disabled={isReadOnlyPermission}>
              Submit
            </Button>
          </Grid>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ColumnOptionsModal;
